/**
 * EnhancedColorPicker
 *
 * Brand swatches + native color wheel + hex input + opacity slider.
 */
import React, { useState, useCallback } from 'react';
import { Label } from '../ui/label';

/* ------------------------------------------------------------------ */
/*  Brand swatches                                                     */
/* ------------------------------------------------------------------ */

const BRAND_SWATCHES = [
  '#F9EF23', // Primary yellow
  '#242424', // Dark
  '#FFFFFF', // White
  '#F9F9F9', // Off-white
  '#6B7280', // Gray
  '#1F2937', // Dark gray
  '#3B82F6', // Blue
  '#10B981', // Green
  '#EF4444', // Red
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0'))
      .join('')
  );
}

function parseColor(color: string): { hex: string; opacity: number } {
  if (!color) return { hex: '#000000', opacity: 100 };

  // rgba(r, g, b, a)
  const rgbaMatch = color.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1]);
    const g = parseInt(rgbaMatch[2]);
    const b = parseInt(rgbaMatch[3]);
    const a = rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1;
    return { hex: rgbToHex(r, g, b), opacity: Math.round(a * 100) };
  }

  // hex
  const hexMatch = color.match(/^#?([0-9a-fA-F]{3,6})$/);
  if (hexMatch) {
    const hex = color.startsWith('#') ? color : `#${color}`;
    return { hex, opacity: 100 };
  }

  return { hex: '#000000', opacity: 100 };
}

function buildColor(hex: string, opacity: number): string {
  if (opacity >= 100) return hex;
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${(opacity / 100).toFixed(2)})`;
}

function isValidHex(hex: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex);
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface EnhancedColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EnhancedColorPicker({
  value,
  onChange,
  label,
}: EnhancedColorPickerProps) {
  const parsed = parseColor(value);
  const [hexInput, setHexInput] = useState(parsed.hex);
  const [opacity, setOpacity] = useState(parsed.opacity);

  // Keep hex input in sync when value prop changes externally
  const { hex: currentHex, opacity: currentOpacity } = parseColor(value);

  const commit = useCallback(
    (hex: string, op: number) => {
      onChange(buildColor(hex, op));
    },
    [onChange]
  );

  const handleSwatchClick = (swatch: string) => {
    setHexInput(swatch);
    setOpacity(100);
    commit(swatch, 100);
  };

  const handleColorWheel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value;
    setHexInput(hex);
    commit(hex, opacity);
  };

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHexInput(e.target.value);
  };

  const handleHexBlur = () => {
    const val = hexInput.startsWith('#') ? hexInput : `#${hexInput}`;
    if (isValidHex(val)) {
      setHexInput(val);
      commit(val, opacity);
    } else {
      // Fallback to last valid hex from prop
      setHexInput(currentHex);
    }
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const op = parseInt(e.target.value);
    setOpacity(op);
    commit(currentHex, op);
  };

  return (
    <div className="space-y-3">
      {label && (
        <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      )}

      {/* Brand swatches */}
      <div className="grid grid-cols-6 gap-1.5">
        {BRAND_SWATCHES.map((swatch) => (
          <button
            key={swatch}
            type="button"
            title={swatch}
            onClick={() => handleSwatchClick(swatch)}
            className={`w-7 h-7 rounded-full cursor-pointer ring-offset-1 hover:ring-2 hover:ring-[#242424] transition-all ${
              currentHex.toLowerCase() === swatch.toLowerCase()
                ? 'ring-2 ring-[#242424]'
                : ''
            }`}
            style={{ backgroundColor: swatch, border: swatch === '#FFFFFF' || swatch === '#F9F9F9' ? '1px solid #e5e7eb' : 'none' }}
          />
        ))}
      </div>

      {/* Native color wheel */}
      <input
        type="color"
        value={currentHex}
        onChange={handleColorWheel}
        className="w-full h-10 rounded cursor-pointer border-0 p-0"
        title="Color wheel"
      />

      {/* Hex input */}
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded border border-border shrink-0"
          style={{ backgroundColor: value || '#000000' }}
        />
        <input
          type="text"
          value={hexInput}
          onChange={handleHexChange}
          onBlur={handleHexBlur}
          placeholder="#000000"
          className="flex-1 px-2 py-1.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono"
        />
      </div>

      {/* Opacity slider */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Opacity</span>
          <span className="text-[11px] font-medium text-foreground">{currentOpacity}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={currentOpacity}
          onChange={handleOpacityChange}
          className="w-full h-1.5 accent-primary cursor-pointer rounded-full"
        />
      </div>
    </div>
  );
}
